import { useCallback, useEffect, useMemo, useState } from 'react';
import { INVESTOWN_CURRENCY } from '@app/common';
import { PersonalAssetWithValueMobxDto } from '../../../../../../models/mobx/dtos/personal-assets/personal-asset-with-value-mobx-dto';
import { useRootStore } from '../../../../../../providers/root-store-provider';

type UsePortfolioAssetsCurrencyModeProps = {
    personalAssets: PersonalAssetWithValueMobxDto[];
    portfolioCurrencyCode: string;
    isExpanded: boolean;
    hasInvestown?: boolean;
};

type UsePortfolioAssetsCurrencyModeReturn = {
    showConvertedValues: boolean;
    toggleValueDisplay?: () => void;
    currencyRatesBySymbol: Record<string, number>;
    hasForeignCurrencyAssets: boolean;
};

export const usePortfolioAssetsCurrencyMode = ({
    personalAssets,
    portfolioCurrencyCode,
    isExpanded,
    hasInvestown = false,
}: UsePortfolioAssetsCurrencyModeProps): UsePortfolioAssetsCurrencyModeReturn => {
    const { apiClient } = useRootStore();
    const [showConvertedValues, setShowConvertedValues] = useState(true);
    const [currencyRatesBySymbol, setCurrencyRatesBySymbol] = useState<Record<string, number>>({});

    const requiredRateSymbols = useMemo(() => {
        const symbols = [...personalAssets]
            .filter((asset) => asset.currencyCode !== portfolioCurrencyCode)
            .map((asset) => `${asset.currencyCode}${portfolioCurrencyCode}`);

        if (hasInvestown && portfolioCurrencyCode !== INVESTOWN_CURRENCY) {
            symbols.push(`${INVESTOWN_CURRENCY}${portfolioCurrencyCode}`);
        }

        return Array.from(new Set(symbols));
    }, [personalAssets, portfolioCurrencyCode, hasInvestown]);

    const hasForeignCurrencyAssets = requiredRateSymbols.length > 0;
    const toggleValueDisplay = useCallback(() => {
        setShowConvertedValues((value) => !value);
    }, []);

    const missingRateSymbols = useMemo(
        () => requiredRateSymbols.filter((symbol) => currencyRatesBySymbol[symbol] === undefined),
        [requiredRateSymbols, currencyRatesBySymbol],
    );

    useEffect(() => {
        let mounted = true;

        if (!isExpanded || !showConvertedValues || missingRateSymbols.length === 0) {
            return () => {
                mounted = false;
            };
        }

        const fetchRates = async () => {
            try {
                const rates = await apiClient.currencyController.currencyControllerGetAllRatesByCurrencyCode({
                    symbols: missingRateSymbols,
                });

                if (!mounted) {
                    return;
                }

                const nextRates: Record<string, number> = {};
                rates.forEach((rate) => {
                    nextRates[rate.symbol] = rate.rate;
                });
                setCurrencyRatesBySymbol((prev) => ({ ...prev, ...nextRates }));
            } catch {
                // noop - keep previously fetched rates
            }
        };

        fetchRates();

        return () => {
            mounted = false;
        };
    }, [apiClient, isExpanded, missingRateSymbols, showConvertedValues]);

    return {
        showConvertedValues,
        toggleValueDisplay: hasForeignCurrencyAssets ? toggleValueDisplay : undefined,
        currencyRatesBySymbol,
        hasForeignCurrencyAssets,
    };
};
